import Card from 'components/card';
import Pagination from 'components/pagination/Pagination';
import { useEffect, useState } from 'react';
import { Info } from 'lucide-react';
import { getBiProductMargin } from 'interfaces/bi';
import { formatCurrency, formatNumber, formatPercent, marginRateBadge } from './helpers';

type SortKey = 'margin' | 'revenue' | 'quantity';

/** Tooltip text when margin is derived from only part of the period's revenue. */
function coverageTitle(r: any): string | null {
    if (r.costCoverage === null || r.costCoverage === undefined || r.costCoverage >= 100) {
        return null;
    }
    return `Marge calculée sur ${formatPercent(r.costCoverage, 0)} du CA de la période (prix d'achat manquant sur le reste des ventes)`;
}

function ProductMarginTable(props: { from: string; to: string }) {
    const { from, to } = props;
    const [rows, setRows] = useState<any[]>([]);
    const [meta, setMeta] = useState<any>(null);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [sort, setSort] = useState<SortKey>('revenue');
    const [onlyWithPurchase, setOnlyWithPurchase] = useState(false);
    const [loading, setLoading] = useState(true);

    const fetchData = async (p = page, l = limit, s = sort, owp = onlyWithPurchase) => {
        setLoading(true);
        const res = await getBiProductMargin({ from, to, page: p, limit: l, sort: s, onlyWithPurchase: owp });
        setRows(res?.data ?? []);
        setMeta(res?.meta ?? null);
        setPage(p);
        setLimit(l);
        setLoading(false);
    };

    useEffect(() => {
        fetchData(1, limit, sort, onlyWithPurchase);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [from, to, sort, onlyWithPurchase]);

    const empty = (
        <p className="py-8 text-center text-sm text-gray-400">
            {onlyWithPurchase
                ? "Aucun produit avec prix d'achat sur la période."
                : 'Aucune donnée sur la période.'}
        </p>
    );

    return (
        <Card extra="p-5 border h-full">
            {/* Header */}
            <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
                <div>
                    <h4 className="text-lg font-bold text-navy-700">Marge par produit</h4>
                    <p className="text-sm text-gray-500">Taux de marque et taux de marge sur la période</p>
                </div>
                <div className="flex flex-wrap items-center gap-3 text-sm">
                    <label className="flex cursor-pointer items-center gap-1.5 text-gray-500">
                        <input
                            type="checkbox"
                            checked={onlyWithPurchase}
                            onChange={(e) => setOnlyWithPurchase(e.target.checked)}
                            className="rounded"
                        />
                        <span>Avec prix d'achat</span>
                    </label>
                    <select
                        value={sort}
                        onChange={(e) => setSort(e.target.value as SortKey)}
                        className="rounded-md border border-gray-300 px-2 py-1 text-xs outline-none focus:border-toola-500"
                    >
                        <option value="revenue">Trier par CA</option>
                        <option value="margin">Trier par marge</option>
                        <option value="quantity">Trier par quantité</option>
                    </select>
                </div>
            </div>

            {loading ? (
                <p className="py-8 text-center text-sm text-gray-400">Chargement...</p>
            ) : rows.length === 0 ? (
                empty
            ) : (
                <>
                    {/* ── Mobile cards ── */}
                    <div className="space-y-2 sm:hidden">
                        {rows.map((r) => (
                            <div
                                key={r.productKey}
                                className="rounded-xl border border-gray-100 bg-gray-50 p-3"
                            >
                                <p
                                    className="text-sm font-semibold text-navy-700"
                                    title={r.name}
                                >
                                    {r.name || '—'}
                                </p>
                                {r.ref && (
                                    <p className="mb-2 text-[11px] text-gray-400">{r.ref}</p>
                                )}
                                <div className="mt-1 grid grid-cols-3 gap-x-2 gap-y-1 text-xs">
                                    <div>
                                        <p className="text-gray-400">Qté</p>
                                        <p className="font-medium text-navy-700">
                                            {formatNumber(r.quantity, 0)}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-gray-400">CA HT</p>
                                        <p className="font-medium text-navy-700">
                                            {formatCurrency(r.revenueHT)}
                                        </p>
                                    </div>
                                    <div>
                                        <p className="text-gray-400">Marge</p>
                                        <p className="font-medium text-navy-700">
                                            {formatCurrency(r.marginAmount)}
                                        </p>
                                    </div>
                                    <div className="col-span-2 mt-1 flex items-center gap-2">
                                        <span
                                            className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${marginRateBadge(r.markupRate)}`}
                                        >
                                            Marque {formatPercent(r.markupRate)}
                                        </span>
                                        <span
                                            className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${marginRateBadge(r.marginRate)}`}
                                        >
                                            Marge {formatPercent(r.marginRate)}
                                        </span>
                                        {coverageTitle(r) && (
                                            <span title={coverageTitle(r) as string} className="shrink-0">
                                                <Info size={14} className="text-gray-400" />
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* ── Desktop table ── */}
                    <div className="hidden sm:block">
                        <table className="w-full table-fixed">
                            <colgroup>
                                <col className="w-[38%]" />
                                <col className="w-[10%]" />
                                <col className="w-[16%]" />
                                <col className="w-[16%]" />
                                <col className="w-[10%]" />
                                <col className="w-[10%]" />
                            </colgroup>
                            <thead>
                                <tr className="border-b border-gray-200 text-left">
                                    <th className="py-2 text-xs font-bold text-gray-600">Produit</th>
                                    <th className="py-2 text-right text-xs font-bold text-gray-600">Qté</th>
                                    <th className="py-2 text-right text-xs font-bold text-gray-600">CA HT</th>
                                    <th className="py-2 text-right text-xs font-bold text-gray-600">Marge</th>
                                    <th className="py-2 text-center text-xs font-bold text-gray-600">Tx marque</th>
                                    <th className="py-2 text-center text-xs font-bold text-gray-600">Tx marge</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr
                                        key={r.productKey}
                                        className="border-b border-gray-100 hover:bg-gray-50"
                                    >
                                        <td className="overflow-hidden py-2 pr-3">
                                            <p
                                                className="truncate text-sm font-medium text-navy-700"
                                                title={r.name}
                                            >
                                                {r.name || '—'}
                                            </p>
                                            {r.ref && (
                                                <p className="truncate text-[11px] text-gray-400">
                                                    {r.ref}
                                                </p>
                                            )}
                                        </td>
                                        <td className="py-2 text-right text-sm text-navy-700">
                                            {formatNumber(r.quantity, 0)}
                                        </td>
                                        <td className="py-2 text-right text-sm text-navy-700">
                                            {formatCurrency(r.revenueHT)}
                                        </td>
                                        <td className="py-2 text-right text-sm text-navy-700">
                                            {formatCurrency(r.marginAmount)}
                                        </td>
                                        <td className="py-2 text-center">
                                            <span
                                                className={`rounded-full px-2 py-0.5 text-xs font-semibold ${marginRateBadge(r.markupRate)}`}
                                            >
                                                {formatPercent(r.markupRate)}
                                            </span>
                                        </td>
                                        <td className="py-2 text-center">
                                            <span className="inline-flex items-center gap-1">
                                                <span
                                                    className={`rounded-full px-2 py-0.5 text-xs font-semibold ${marginRateBadge(r.marginRate)}`}
                                                >
                                                    {formatPercent(r.marginRate)}
                                                </span>
                                                {coverageTitle(r) && (
                                                    <span title={coverageTitle(r) as string} className="shrink-0">
                                                        <Info size={13} className="text-gray-400" />
                                                    </span>
                                                )}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            <Pagination
                extra="mt-3"
                text={false}
                type="produits"
                maxItems={meta ? meta.total : 0}
                itemsPerPage={[10, 25, 50]}
                page={page}
                defaultItemsPerPage={limit}
                onChange={(p: number, l: number) => fetchData(p, l, sort, onlyWithPurchase)}
            />
        </Card>
    );
}

export default ProductMarginTable;
